import {AABB, CONSTANTS, COLLISION, Script, System, VECTOR3} from '../../index.js';

/**
 * Creates Hypercube.js AABB collision systems.
 * @example
 * const systemcollisionaabb = new SystemCollisionAABB();
 *
 * // called by an update loop
 * systemcollisionaabb.tick();
 */
class SystemCollisionAABB extends System {

    /**
     * Creates a new Hypercube.js AABB collision system.
     */
    constructor() {

        super();
    }

    /**
     * Destroys the system.
     * @public
     */
    destroy() {

        console.log('Hypercube.js - AABB collision system - destroy()');
    }

    // @TODO
    $distance([xa, ya, za], [xb, yb, zb]) {

        return Math.abs(xa - xb) + Math.abs(ya - yb) + Math.abs(za - zb);

        return Math.sqrt(
            Math.pow(xa - xb, 2) + Math.pow(ya - yb, 2) + Math.pow(za - zb, 2)
            )
    }

    /**
     * Forwards the time from the previous state while performing continuous collision detection.
     * @param {number} $timeelapsed The elapsed time since the last collision resolution.
     * @private
     */
    $forward($timeelapsed) {

        const nodes = this.scene.getnodeswithcomponent(this.scene.noderoot, 'collideraabb');

        let pairs = [];

        for (let currentindex = 0; currentindex < nodes.length; currentindex += 1) {

            const current = nodes[currentindex];
            const currentcomponentcollideraabb = current.getcomponent('collideraabb');

            if (typeof currentcomponentcollideraabb === 'undefined') {

                continue;
            }

            const currentcomponentbefore = current.getcomponent('before');

            for (let otherindex = currentindex + 1; otherindex < nodes.length; otherindex += 1) {

                const other = nodes[otherindex];
                const othercomponentcollideraabb = other.getcomponent('collideraabb');

                if (typeof othercomponentcollideraabb === 'undefined') {

                    continue;
                }

                const othercomponentbefore = other.getcomponent('before');

                if (currentcomponentcollideraabb.type === 'static'
                && othercomponentcollideraabb.type === 'static') {

                    continue;
                }

                const [xa, ya, za] = current.translation;
                const [xb, yb, zb] = other.translation;

                if (current.name !== 'plane1080x1080'   // @TODO : only for LD53
                && other.name !== 'plane1080x1080'
                && this.$distance([xa, ya, za], [xb, yb, zb]) > 3) {

                    continue;
                }

                const currentaabbbefore = new AABB(

                    VECTOR3.add(currentcomponentcollideraabb.min, currentcomponentbefore),
                    VECTOR3.add(currentcomponentcollideraabb.max, currentcomponentbefore)
                );

                const currentaabbafter = new AABB(

                    VECTOR3.add(currentcomponentcollideraabb.min, current.translation),
                    VECTOR3.add(currentcomponentcollideraabb.max, current.translation)
                );

                const currentaabbswept = COLLISION.aabbminimumcontainer(currentaabbbefore, currentaabbafter);

                const otheraabbbefore = new AABB(

                    VECTOR3.add(othercomponentcollideraabb.min, othercomponentbefore),
                    VECTOR3.add(othercomponentcollideraabb.max, othercomponentbefore)
                );

                const otheraabbafter = new AABB(

                    VECTOR3.add(othercomponentcollideraabb.min, other.translation),
                    VECTOR3.add(othercomponentcollideraabb.max, other.translation)
                );

                const otheraabbswept = COLLISION.aabbminimumcontainer(otheraabbbefore, otheraabbafter);

                if (COLLISION.aabbcollidestrict(currentaabbswept, otheraabbswept) === false) {

                    continue;
                }

                const timecollision = COLLISION.aabbsweptcollision(

                    currentaabbbefore,
                    currentaabbafter,
                    otheraabbbefore,
                    otheraabbafter
                );

                if (timecollision === 1) {

                    continue;
                }

                const pair = {

                    timecollision: timecollision,
                    current: current,
                    other: other,
                };

                pairs.push(pair);
            }
        }

        if (pairs.length === 0) {

            this.scene.getnodeswithcomponent(this.scene.noderoot, 'collideraabb').forEach(($current) => {

                $current.setcomponent('before', VECTOR3.clone($current.translation));
            });

            return;
        }

        if (pairs.length > 1) {

            pairs = pairs.sort(($a, $b) => {

                return $a.timecollision - $b.timecollision;
            });

            pairs = pairs.filter(($a) => {

                return $a.timecollision === pairs[0].timecollision;
            });
        }

        const timecollision = Math.max(pairs[0].timecollision - CONSTANTS.PRECISION_EPSILON_8, 0);
        const timebeforecollision = timecollision;
        const timeaftercollision = 1 - timebeforecollision;

        this.scene.getnodeswithcomponent(this.scene.noderoot, 'collideraabb').forEach(($current) => {

            const componentbefore = $current.getcomponent('before');

            const velocity = VECTOR3.subtract($current.translation, componentbefore);
            const velocitybeforecollision = VECTOR3.multiplyscalar(velocity, timebeforecollision);
            const translationbeforecollision = VECTOR3.add(componentbefore, velocitybeforecollision);

            $current.setcomponent('before', translationbeforecollision);
        });

        pairs.forEach(($pair) => {

            const {current, other} = $pair;

            const currentcomponentbefore = current.getcomponent('before');
            const othercomponentbefore = other.getcomponent('before');

            const currentcomponentcollideraabb = current.getcomponent('collideraabb');
            const othercomponentcollideraabb = other.getcomponent('collideraabb');

            const currentvelocityreverse = VECTOR3.subtract(currentcomponentbefore, current.translation);
            const othervelocityreverse = VECTOR3.subtract(othercomponentbefore, other.translation);

            const currentvelocity = VECTOR3.subtract(current.translation, currentcomponentbefore);
            const othervelocity = VECTOR3.subtract(other.translation, othercomponentbefore);

            current.translate(currentvelocityreverse);
            other.translate(othervelocityreverse);

            const currentaabb = new AABB(

                VECTOR3.add(currentcomponentcollideraabb.min, current.translation),
                VECTOR3.add(currentcomponentcollideraabb.max, current.translation)
            );

            const otheraabb = new AABB(

                VECTOR3.add(othercomponentcollideraabb.min, other.translation),
                VECTOR3.add(othercomponentcollideraabb.max, other.translation)
            );

            const overlapx = COLLISION.aabboverlapstrictx(currentaabb, otheraabb);
            const overlapy = COLLISION.aabboverlapstricty(currentaabb, otheraabb);
            const overlapz = COLLISION.aabboverlapstrictz(currentaabb, otheraabb);

            current.translate(

                [
                    overlapx === true ? currentvelocity[0] * timeaftercollision : 0,
                    overlapy === true ? currentvelocity[1] * timeaftercollision : 0,
                    overlapz === true ? currentvelocity[2] * timeaftercollision : 0
                ]
            );

            other.translate(

                [
                    overlapx === true ? othervelocity[0] * timeaftercollision : 0,
                    overlapy === true ? othervelocity[1] * timeaftercollision : 0,
                    overlapz === true ? othervelocity[2] * timeaftercollision : 0
                ]
            );

            if (current.script instanceof Script) {

                current.script.oncollide($timeelapsed * timebeforecollision, current, other);
            }

            if (other.script instanceof Script) {

                other.script.oncollide($timeelapsed * timebeforecollision, other, current);
            }
        });

        this.$forward($timeelapsed * timeaftercollision);
    }

    /**
     * Starts the system.
     * @public
     */
    start() {

        console.log('Hypercube.js - AABB collision system - start()');

        this.scene.getnodeswithcomponent(this.scene.noderoot, 'collideraabb').forEach(($current) => {

            $current.setcomponent('before', VECTOR3.clone($current.translation));
        });
    }

    /**
     * Updates the system once.
     * @param {number} $timeframe The timeframe duration (in ms).
     * @public
     */
    tick($timeframe) {

        this.$forward($timeframe);
    }
}

export {

    SystemCollisionAABB
};

export default SystemCollisionAABB;
